import { client } from "./client";
import type { CustomerUpdate } from "@/providers/smarta-accounts/accounts-api-client";
import type { Firm } from "@/modules/firms";

/**
 * Assign a customer's managing operator (Key Account Manager) in smarta-accounts
 * via `PUT /api/customers/{id}/manager`. The customer is addressed by its
 * publicId (== kontax `User.publicId`); `operatorId` is the accounts operator id.
 *
 * This hits the kontax accounts gateway, which OVERRIDES this route to also
 * cascade the assignment to the user's open tax cases (the legacy side effect),
 * mirroring the banking create-mandate override. When kontax is retired the call
 * site stays identical and only points at the accounts service directly.
 */
export const setCustomerManager = async (
  customerPublicId: string,
  operatorId: string
): Promise<void> => {
  const { error } = await client.PUT("/api/customers/{id}/manager", {
    params: { path: { id: customerPublicId } },
    body: { operatorId },
  });
  if (error) {
    throw error;
  }
};

/**
 * Clear a customer's managing operator via `DELETE /api/customers/{id}/manager`.
 * (The gateway override does NOT cascade to tax cases on clear.)
 */
export const clearCustomerManager = async (
  customerPublicId: string
): Promise<void> => {
  const { error } = await client.DELETE("/api/customers/{id}/manager", {
    params: { path: { id: customerPublicId } },
  });
  if (error) {
    throw error;
  }
};

export const updateCustomerFirm = async (
  customerPublicId: string,
  firm: Firm
): Promise<void> => {
  const { error } = await client.PUT("/api/customers/{id}", {
    params: { path: { id: customerPublicId } },
    body: { firm } as CustomerUpdate,
  });
  if (error) {
    throw error;
  }
};
